import React from 'react';
import { sanitizeStyleValue } from '../../Sanitizer.jsx';
import { useTemplateContactInfo, useTemplateInquiryForm } from '../../TemplateContactUtils.jsx';
import {
  applyFutureSectionStyle,
  colorWithOpacity,
  getBlocks,
  getImageUrl,
} from '../shared/templateSectionUtils.jsx';
import {
  ArrowRight,
  Building2,
  Calendar,
  CalendarDays,
  Car,
  CheckCircle,
  Clock,
  Coffee,
  CreditCard,
  FileText,
  Headphones,
  Mail,
  MapPin,
  Monitor,
  Phone,
  Printer,
  Receipt,
  Search,
  Settings,
  Shield,
  Star,
  Target,
  Users,
  Wifi,
  Zap,
} from 'lucide-react';

const iconKey = (value = '') => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const templateOverlayColorWithOpacity = (color = '#000000', opacity = 0.45) => (
  String(color).startsWith('#')
    ? `${sanitizeStyleValue(color)}${Math.round(Number(opacity || 0) * 255).toString(16).padStart(2, '0')}`
    : sanitizeStyleValue(color)
);

const flexTheme = (theme = {}) => {
  const primary = theme.primary || '#2563eb';
  const secondary = theme.secondary || '#0f172a';
  const accent = theme.accent || '#1d4ed8';
  return {
    primary,
    secondary,
    accent,
    primarySoft: theme.primarySoft || colorWithOpacity(primary, 0.14),
    primaryMuted: theme.primaryMuted || colorWithOpacity(primary, 0.28),
    accentSoft: theme.accentSoft || colorWithOpacity(accent, 0.14),
    background: theme.background || '#f8fafc',
    surface: theme.surface || '#ffffff',
    color: theme.color || '#1e293b',
    mutedColor: theme.mutedColor || '#64748b',
    borderColor: theme.borderColor || '#e2e8f0',
    headingFontFamily: theme.headingFontFamily || 'Inter, Arial, sans-serif',
    fontFamily: theme.fontFamily || 'Inter, Arial, sans-serif',
    pageWidth: theme.pageWidth || '1280px',
    buttonRadius: theme.buttonRadius || '12px',
    cardRadius: theme.cardRadius || '20px',
    cardShadow: theme.cardShadow || '0 20px 45px rgba(15, 23, 42, 0.12)',
    imageRadius: theme.imageRadius || '20px'
  };
};

const flexSectionStyle = (section, theme = {}) => {
  const t = flexTheme(theme);
  const style = section?.style || {};
  const next = {
    backgroundColor: style.backgroundColor || t.background,
    color: style.color || t.color,
    padding: style.padding || '80px 24px',
    minHeight: style.minHeight,
    margin: style.margin,
    borderRadius: style.borderRadius,
    border: style.border,
    boxShadow: style.boxShadow,
    textAlign: style.textAlign || section?.layout?.align,
    fontFamily: t.fontFamily
  };
  if (style.backgroundType === 'gradient' && style.backgroundGradient) {
    next.background = sanitizeStyleValue(style.backgroundGradient);
  }
  if (style.backgroundImage && style.backgroundType !== 'none') {
    const overlay = templateOverlayColorWithOpacity(style.overlayColor || '#000000', style.overlayOpacity || 0.45);
    next.backgroundImage = `${style.backgroundType === 'image' && style.overlayOpacity !== '0' ? `linear-gradient(${overlay}, ${overlay}), ` : ''}url(${sanitizeStyleValue(style.backgroundImage)})`;
    next.backgroundSize = sanitizeStyleValue(style.backgroundSize || 'cover');
    next.backgroundPosition = sanitizeStyleValue(style.backgroundPosition || 'center');
    next.backgroundRepeat = sanitizeStyleValue(style.backgroundRepeat || 'no-repeat');
  }
  if (style.backgroundType === 'none') {
    delete next.background;
    delete next.backgroundColor;
    delete next.backgroundImage;
  }
  return applyFutureSectionStyle(next, section, t);
};

const flexContainerStyle = (section, theme = {}) => ({
  maxWidth: section?.layout?.width === 'full'
    ? 'none'
    : section?.layout?.width === 'narrow'
      ? section?.style?.maxWidth || '920px'
      : section?.style?.maxWidth || flexTheme(theme).pageWidth,
  margin: '0 auto',
  width: '100%'
});

const FlexOfficeButton = ({ label, url, variant = 'primary', theme, section }) => {
  if (!label) return null;
  const t = flexTheme(theme);
  const isOutline = variant === 'outline' || variant === 'secondary';
  const color = section?.style?.accentColor || t.primary;
  return (
    <a
      href={url || '#'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 46,
        padding: '0 26px',
        borderRadius: t.buttonRadius,
        border: `1px solid ${isOutline ? t.primarySoft : 'transparent'}`,
        background: isOutline ? t.surface : `linear-gradient(135deg, ${color}, ${t.accent})`,
        color: isOutline ? color : '#ffffff',
        textDecoration: 'none',
        fontWeight: 700,
        boxShadow: isOutline ? '0 8px 18px rgba(15, 23, 42, 0.08)' : `0 12px 26px ${colorWithOpacity(color, 0.24)}`
      }}
    >
      {label}
    </a>
  );
};

const flexGradientColors = (gradient = 'from-blue-400 to-blue-600', theme = {}) => {
  if (gradient.includes('emerald')) return ['#34d399', '#059669'];
  if (gradient.includes('violet')) return ['#a78bfa', '#7c3aed'];
  if (gradient.includes('amber')) return ['#fbbf24', '#d97706'];
  if (gradient.includes('rose')) return ['#fb7185', '#e11d48'];
  return [theme.primary || '#2563eb', theme.accent || theme.primary || '#1d4ed8'];
};

const flexOfficeIconMap = {
  arrowright: ArrowRight,
  building: Building2,
  building2: Building2,
  calendar: Calendar,
  calendardays: CalendarDays,
  car: Car,
  check: CheckCircle,
  checkcircle: CheckCircle,
  clock: Clock,
  coffee: Coffee,
  creditcard: CreditCard,
  desk: Monitor,
  filetext: FileText,
  headphones: Headphones,
  mail: Mail,
  mappin: MapPin,
  meeting: Building2,
  monitor: Monitor,
  phone: Phone,
  printer: Printer,
  receipt: Receipt,
  search: Search,
  settings: Settings,
  shield: Shield,
  sparkles: Star,
  star: Star,
  target: Target,
  users: Users,
  wifi: Wifi,
  zap: Zap
};

const FlexOfficeIcon = ({ name, size = 22, color = 'currentColor' }) => {
  const Icon = flexOfficeIconMap[iconKey(name)] || Star;
  return <Icon aria-hidden="true" size={size} color={color} strokeWidth={2.25} />;
};

const FlexOfficeHeroSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const c = section.content || {};
  const image = getImageUrl(getBlocks(section, 'images')[0]) || getImageUrl(c) || '';
  const heroBackground = section.style?.backgroundType === 'image' && section.style?.backgroundImage
    ? {}
    : { backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.78), rgba(15,23,42,.62), rgba(15,23,42,.12)), url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  return (
    <section style={{ ...flexSectionStyle(section, t), ...heroBackground, minHeight: section.style?.minHeight || '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
      <div style={{ maxWidth: 920, padding: '88px 24px' }}>
        {c.badge && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '10px 18px', borderRadius: 999, background: `linear-gradient(90deg, ${t.primarySoft}, ${t.surface})`, color: '#334155', border: `1px solid ${t.borderColor}`, fontSize: 13, fontWeight: 700 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: '#34d399' }} />{c.badge}</span>}
        <h1 style={{ margin: '26px auto 18px', maxWidth: 850, color: '#fff', fontSize: 'clamp(38px, 5vw, 64px)', lineHeight: 1.08, letterSpacing: '-.02em', fontWeight: 800 }}>{c.titlePrefix} <span style={{ background: `linear-gradient(135deg, ${t.accent}, ${t.primary})`, WebkitBackgroundClip: 'text', color: 'transparent' }}>{c.titleAccent}</span></h1>
        <p style={{ maxWidth: 760, margin: '0 auto 30px', color: '#e2e8f0', fontSize: 18, lineHeight: 1.7, fontWeight: 600 }}>{c.subtitle}</p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 16 }}>
          <FlexOfficeButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={t} section={section} />
          <FlexOfficeButton label={c.secondaryActionLabel} url={c.secondaryActionUrl} variant="outline" theme={t} section={section} />
        </div>
      </div>
    </section>
  );
};

const FlexOfficeAboutSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const c = section.content || {};
  const imageUrl = getImageUrl(c);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div className="flex-office-template-grid" style={{ ...flexContainerStyle(section, t), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
        <div style={{ position: 'relative', padding: '0 24px 28px 54px' }}>
          {imageUrl && <img src={imageUrl} alt={c.imageAlt || ''} style={{ width: '100%', borderRadius: 22, border: `1px solid ${t.borderColor}`, boxShadow: t.cardShadow }} />}
          <div style={{ position: 'absolute', left: 0, bottom: 0, maxWidth: 420, background: 'linear-gradient(135deg, #0f172a, #1e293b)', borderRadius: 22, padding: 24, color: '#fff', boxShadow: t.cardShadow }}>
            <p style={{ margin: 0, lineHeight: 1.6, fontSize: 17 }}>{c.quote}</p>
          </div>
        </div>
        <div>
          {c.eyebrow && <span style={{ display: 'inline-flex', padding: '8px 16px', borderRadius: 999, background: t.primarySoft, color: t.primary, fontWeight: 800, marginBottom: 22 }}>{c.eyebrow}</span>}
          <h2 style={{ margin: '0 0 24px', color: t.secondary, fontSize: 'clamp(40px, 5vw, 66px)', lineHeight: 1.06 }}>{c.titlePrefix}<br /><span style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, WebkitBackgroundClip: 'text', color: 'transparent' }}>{c.titleAccent}</span></h2>
          <p style={{ color: t.mutedColor, fontSize: 18, lineHeight: 1.75, marginBottom: 30 }}>{c.description}</p>
          <FlexOfficeButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={t} section={section} />
        </div>
      </div>
    </section>
  );
};

const FlexOfficeTimelineSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  return (
    <section style={{ ...flexSectionStyle(section, t), position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at top right, ${t.primarySoft}, transparent 40%)` }} />
      <div style={{ ...flexContainerStyle(section, t), position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 58 }}>
          {section.content?.eyebrow && <span style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 999, background: t.primarySoft, border: `1px solid ${t.primaryMuted}`, color: t.accent, fontWeight: 800 }}>{section.content.eyebrow}</span>}
          <h2 style={{ margin: '24px 0 0', color: '#fff', fontSize: 'clamp(40px, 5vw, 66px)', lineHeight: 1.08 }}>{section.content?.titlePrefix}<br /><span style={{ background: `linear-gradient(135deg, ${t.accent}, ${t.primary})`, WebkitBackgroundClip: 'text', color: 'transparent' }}>{section.content?.titleAccent}</span></h2>
        </div>
        <div className="flex-office-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 24 }}>
          {getBlocks(section).map((item, index) => {
            const [from, to] = flexGradientColors(item.gradient, t);
            return (
              <article key={`${item.title}-${index}`} style={{ background: 'rgba(30,41,59,.68)', border: '1px solid rgba(148,163,184,.22)', borderRadius: 22, overflow: 'hidden', color: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,.25)' }}>
                <div style={{ height: 8, background: `linear-gradient(90deg, ${from}, ${to})` }} />
                <div style={{ padding: 28, textAlign: 'center' }}>
                  <div style={{ margin: '0 auto 22px', width: 64, height: 64, display: 'grid', placeItems: 'center', borderRadius: 18, background: `linear-gradient(135deg, ${from}, ${to})`, color: '#fff', fontWeight: 900 }}><FlexOfficeIcon name={item.icon} size={30} /></div>
                  <span style={{ display: 'inline-grid', placeItems: 'center', width: 32, height: 32, borderRadius: 999, background: t.primarySoft, color: t.accent, fontWeight: 900 }}>{index + 1}</span>
                  <h3 style={{ margin: '18px 0 10px', fontSize: 22 }}>{item.title}</h3>
                  <p style={{ margin: 0, color: '#cbd5e1', lineHeight: 1.6 }}>{item.description}</p>
                </div>
              </article>
            );
          })}
        </div>
        <div style={{ textAlign: 'center', marginTop: 48 }}><FlexOfficeButton label={section.content?.primaryActionLabel} url={section.content?.primaryActionUrl} theme={t} section={section} /></div>
      </div>
    </section>
  );
};

const FlexOfficeWorkingHoursSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const c = section.content || {};
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const working = Array.isArray(c.workingDays) ? c.workingDays : [];
  return (
    <section style={flexSectionStyle(section, t)}>
      <div style={{ ...flexContainerStyle(section, t), maxWidth: 980 }}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 999, background: t.surface, border: `1px solid ${t.primarySoft}`, color: '#334155', fontWeight: 700 }}>{c.eyebrow}</span>
          <h2 style={{ margin: '18px 0 10px', color: t.secondary, fontSize: 'clamp(38px, 5vw, 58px)' }}>{c.titlePrefix} <span style={{ color: t.primary }}>{c.titleAccent}</span></h2>
          <p style={{ color: t.mutedColor }}>{c.subtitle}</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 28, overflow: 'hidden', boxShadow: t.cardShadow, border: `1px solid ${t.borderColor}` }}>
          <div style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, padding: 34, display: 'flex', justifyContent: 'center', gap: 64, color: '#fff', flexWrap: 'wrap' }}>
            {[{ label: c.openLabel, value: c.openTime }, { label: c.closeLabel, value: c.closeTime }].map((item) => <div key={item.label} style={{ textAlign: 'center' }}><div style={{ color: 'rgba(255,255,255,.78)', fontSize: 14, marginBottom: 8 }}>{item.label}</div><div style={{ fontSize: 46, fontWeight: 900 }}>{item.value}</div></div>)}
          </div>
          <div style={{ padding: 34 }}>
            <h3 style={{ margin: '0 0 20px', color: t.secondary }}>{c.scheduleTitle || 'Weekly Schedule'}</h3>
            <div className="flex-office-template-days-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 10 }}>
              {days.map((day) => {
                const active = working.includes(day);
                return <div key={day} style={{ aspectRatio: '1/1', borderRadius: 18, display: 'grid', placeItems: 'center', background: active ? 'linear-gradient(135deg, #4ade80, #10b981)' : '#f1f5f9', color: active ? '#fff' : '#94a3b8', fontWeight: 900 }}>{day}</div>;
              })}
            </div>
            <div style={{ marginTop: 28, padding: 18, borderRadius: 18, border: `1px solid ${t.primarySoft}`, background: t.primarySoft, color: '#334155' }}>{c.infoText}</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 28 }}><FlexOfficeButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={t} section={section} /></div>
      </div>
    </section>
  );
};

const FlexOfficeFeaturesSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  return (
    <section style={{ ...flexSectionStyle(section, t), position: 'relative', overflow: 'hidden' }}>
      <div style={flexContainerStyle(section, t)}>
        <div style={{ textAlign: 'center', maxWidth: 860, margin: '0 auto 56px' }}>
          <span style={{ display: 'inline-block', padding: '8px 18px', borderRadius: 999, background: t.primarySoft, color: t.accent, border: `1px solid ${t.primaryMuted}`, fontWeight: 800 }}>{section.content?.eyebrow}</span>
          <h2 style={{ margin: '24px 0 18px', color: '#fff', fontSize: 'clamp(38px, 5vw, 64px)', lineHeight: 1.1 }}>{section.content?.titlePrefix} <span style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, WebkitBackgroundClip: 'text', color: 'transparent' }}>{section.content?.titleAccent}</span></h2>
          <p style={{ color: '#cbd5e1', fontSize: 19, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
        </div>
        <div className="flex-office-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 26 }}>
          {getBlocks(section).map((item, index) => {
            const image = getImageUrl(item);
            const [from, to] = flexGradientColors(item.gradient, t);
            return (
              <article key={`${item.title}-${index}`} style={{ background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 22, overflow: 'hidden', color: '#fff' }}>
                {image && <img src={image} alt={item.title || ''} style={{ width: '100%', height: 158, objectFit: 'cover', filter: 'brightness(.78)' }} />}
                <div style={{ padding: 24 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 14, display: 'grid', placeItems: 'center', background: `linear-gradient(135deg, ${from}, ${to})`, marginBottom: 18, fontWeight: 900 }}><FlexOfficeIcon name={item.icon} size={24} /></div>
                  <h3 style={{ margin: '0 0 12px', fontSize: 22 }}>{item.title}</h3>
                  <p style={{ margin: 0, color: '#cbd5e1', lineHeight: 1.65 }}>{item.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FlexOfficeCtaSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const c = section.content || {};
  return (
    <section style={{ ...flexSectionStyle(section, t), textAlign: 'center' }}>
      <div style={{ ...flexContainerStyle(section, t), maxWidth: 900 }}>
        {c.eyebrow && <span style={{ display: 'inline-flex', padding: '8px 14px', borderRadius: 999, background: t.primarySoft, color: t.primary, fontWeight: 800, fontSize: 13 }}>{c.eyebrow}</span>}
        <h2 style={{ margin: '20px 0 16px', color: t.secondary, fontSize: 'clamp(34px, 5vw, 56px)', lineHeight: 1.14 }}>{c.titlePrefix}<br /><span style={{ color: t.primary }}>{c.titleAccent}</span></h2>
        <p style={{ color: t.mutedColor, fontSize: 18, lineHeight: 1.7, maxWidth: 720, margin: '0 auto 32px' }}>{c.subtitle}</p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 14 }}>
          <FlexOfficeButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={t} section={section} />
          <FlexOfficeButton label={c.secondaryActionLabel} url={c.secondaryActionUrl} variant="outline" theme={t} section={section} />
        </div>
      </div>
    </section>
  );
};

const FlexOfficeServicesPageSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div style={flexContainerStyle(section, t)}>
        <div style={{ textAlign: 'center', marginBottom: 44 }}>
          <h1 style={{ color: t.secondary, fontSize: 44, margin: '0 0 12px' }}>{section.content?.title}</h1>
          <p style={{ color: t.mutedColor, fontSize: 18 }}>{section.content?.subtitle}</p>
        </div>
        <div className="flex-office-template-filter-row" style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 18, background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 22, padding: 22, boxShadow: t.cardShadow, marginBottom: 34 }}>
          <input readOnly placeholder={section.content?.searchPlaceholder || 'Search products...'} style={{ border: `1px solid ${t.borderColor}`, borderRadius: 14, padding: '14px 16px' }} />
          <select readOnly style={{ border: `1px solid ${t.borderColor}`, borderRadius: 14, padding: '14px 16px', background: '#fff' }}><option>{section.content?.categoryLabel || 'All Categories'}</option></select>
        </div>
        <div className="flex-office-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 28 }}>
          {getBlocks(section).map((item, index) => {
            const image = getImageUrl(item);
            return (
              <article key={`${item.title}-${index}`} style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 22, overflow: 'hidden', boxShadow: t.cardShadow }}>
                {image && <img src={image} alt={item.title || ''} style={{ width: '100%', height: 220, objectFit: 'cover' }} />}
                <div style={{ padding: 24 }}>
                  <h3 style={{ color: t.secondary, fontSize: 22, margin: 0 }}>{item.title}</h3>
                  <p style={{ color: t.mutedColor }}>{item.description}</p>
                  <strong style={{ color: t.secondary }}>{item.price} <span style={{ color: t.mutedColor, fontWeight: 500 }}>{item.period}</span></strong>
                  <div style={{ marginTop: 18 }}><FlexOfficeButton label={item.buttonLabel || 'View Details'} url={item.url} theme={t} section={section} /></div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FlexOfficeServiceDetailSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const c = section.content || {};
  const image = getImageUrl(c);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div className="flex-office-template-grid" style={{ ...flexContainerStyle(section, t), display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 46, alignItems: 'start' }}>
        <div>{image && <img src={image} alt={c.title || ''} style={{ width: '100%', borderRadius: 22, boxShadow: t.cardShadow, objectFit: 'cover', maxHeight: 520 }} />}</div>
        <div style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 22, padding: 30, boxShadow: t.cardShadow }}>
          {c.eyebrow && <span style={{ color: t.primary, fontWeight: 800, letterSpacing: '.08em' }}>{c.eyebrow}</span>}
          <h1 style={{ color: t.secondary, fontSize: 'clamp(36px, 5vw, 58px)', margin: '12px 0' }}>{c.title}</h1>
          <p style={{ color: t.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{c.subtitle}</p>
          <div style={{ color: t.secondary, fontSize: 30, fontWeight: 900, margin: '26px 0' }}>{c.priceLabel}{c.periodLabel && <span style={{ color: t.mutedColor, fontSize: 16, fontWeight: 600 }}> {c.periodLabel}</span>}</div>
          <div className="flex-office-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12, marginBottom: 24 }}>
            {getBlocks(section).map((item, index) => <div key={`${item.title}-${index}`} style={{ border: `1px solid ${t.borderColor}`, borderRadius: 14, padding: 14 }}><FlexOfficeIcon name={item.icon} color={t.primary} /><strong style={{ display: 'block', marginTop: 8, color: t.secondary }}>{item.title}</strong><p style={{ margin: '4px 0 0', color: t.mutedColor, fontSize: 13 }}>{item.description}</p></div>)}
          </div>
          <FlexOfficeButton label={c.primaryActionLabel} url={c.primaryActionUrl} theme={t} section={section} />
        </div>
      </div>
    </section>
  );
};

const FlexOfficeBookingSearchSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const fields = [
    { label: section.content?.locationLabel, placeholder: 'Downtown Campus' },
    { label: section.content?.dateLabel, placeholder: 'Today' },
    { label: section.content?.durationLabel, placeholder: '2 hours' },
    { label: section.content?.spaceLabel || section.content?.typeLabel, placeholder: 'Meeting Room' }
  ].filter((field) => field.label);

  return (
    <section style={flexSectionStyle(section, t)}>
      <div className="flex-office-template-grid" style={{ ...flexContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 44, alignItems: 'center' }}>
        <div>
          {section.content?.eyebrow && <span style={{ color: t.primary, fontWeight: 800 }}>{section.content.eyebrow}</span>}
          <h1 style={{ color: t.secondary, fontSize: 'clamp(42px, 6vw, 70px)', lineHeight: 1.04, margin: '12px 0 18px' }}>{section.content?.title}</h1>
          <p style={{ color: t.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{section.content?.subtitle}</p>
        </div>
        <div style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 24, padding: 28, boxShadow: t.cardShadow }}>
          {fields.map((field, index) => <label key={`${field.label}-${index}`} style={{ display: 'grid', gap: 8, marginBottom: 14, color: t.secondary, fontWeight: 800 }}>{field.label}<input readOnly placeholder={field.placeholder} style={{ border: `1px solid ${t.borderColor}`, borderRadius: 14, padding: 14 }} /></label>)}
          <FlexOfficeButton label={section.content?.primaryActionLabel} url={section.content?.primaryActionUrl || '/our-services'} theme={t} section={section} />
          {getBlocks(section).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 18 }}>
              {getBlocks(section).slice(0, 4).map((item, index) => (
                <div key={`${item.title}-${index}`} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 10, alignItems: 'center', border: `1px solid ${t.borderColor}`, borderRadius: 14, padding: 12, color: t.secondary }}>
                  <span style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 10, background: t.primarySoft, color: t.primary }}><FlexOfficeIcon name={item.icon} size={16} /></span>
                  <span style={{ minWidth: 0 }}><strong style={{ display: 'block', fontSize: 13 }}>{item.title}</strong><small style={{ color: t.mutedColor }}>{item.description}</small></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const FlexOfficeBookingsSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div style={flexContainerStyle(section, t)}>
        <h1 style={{ color: t.secondary, fontSize: 44, margin: '0 0 10px' }}>{section.content?.title}</h1>
        <p style={{ color: t.mutedColor, fontSize: 18, marginBottom: 32 }}>{section.content?.subtitle}</p>
        {getBlocks(section).length ? (
          <div className="flex-office-template-card-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 22 }}>
            {getBlocks(section).map((item, index) => <article key={`${item.title}-${index}`} style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 20, padding: 24, boxShadow: t.cardShadow }}><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}><span style={{ display: 'grid', placeItems: 'center', width: 42, height: 42, borderRadius: 12, background: t.primarySoft, color: t.primary }}><FlexOfficeIcon name={item.icon || 'Calendar'} size={18} /></span><span style={{ display: 'inline-flex', padding: '6px 10px', borderRadius: 999, background: t.primarySoft, color: t.primary, fontWeight: 800, fontSize: 12 }}>{item.badge}</span></div><h3 style={{ color: t.secondary }}>{item.title}</h3><p style={{ color: t.mutedColor }}>{item.description}</p><strong>{item.price}</strong><p style={{ color: t.mutedColor }}>{item.location}</p></article>)}
          </div>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 20, padding: 28, color: t.mutedColor, boxShadow: t.cardShadow }}>{section.content?.emptyStateTitle || 'Bookings will appear here'}</div>
        )}
      </div>
    </section>
  );
};

const FlexOfficeInvoiceSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div className="flex-office-template-grid" style={{ ...flexContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.8fr 1.2fr', gap: 34 }}>
        <div><h1 style={{ color: t.secondary, fontSize: 44, margin: 0 }}>{section.content?.title}</h1><p style={{ color: t.mutedColor, fontSize: 18 }}>{section.content?.subtitle}</p><div style={{ padding: 26, borderRadius: 22, background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, color: '#fff', marginTop: 28 }}><p>Total</p><strong style={{ fontSize: 42 }}>{section.content?.totalLabel}</strong><p>{section.content?.statusLabel}</p></div>{section.content?.primaryActionLabel && <div style={{ marginTop: 20 }}><FlexOfficeButton label={section.content.primaryActionLabel} url={section.content.primaryActionUrl} theme={t} section={section} /></div>}</div>
        <div style={{ display: 'grid', gap: 16 }}>{getBlocks(section).map((item, index) => <div key={`${item.title}-${index}`} style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto', gap: 14, alignItems: 'center', background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 18, padding: 18 }}><span style={{ display: 'grid', placeItems: 'center', width: 44, height: 44, borderRadius: 12, background: t.primarySoft, color: t.primary }}><FlexOfficeIcon name={item.icon || 'Receipt'} size={18} /></span><div><strong style={{ color: t.secondary }}>{item.title}</strong><p style={{ color: t.mutedColor, margin: '4px 0 0' }}>{item.description}</p></div><strong>{item.price}</strong></div>)}</div>
      </div>
    </section>
  );
};

const FlexOfficeContactSection = ({ section, theme }) => {
  const t = flexTheme(theme);
  const content = section.content || {};
  const contactInfo = useTemplateContactInfo(content);
  const c = { ...content, phoneValue: contactInfo.phoneValue, emailValue: contactInfo.emailValue, addressValue: contactInfo.addressValue };
  const fields = getBlocks(section, 'fields');
  const inquiryForm = useTemplateInquiryForm(fields);
  return (
    <section style={flexSectionStyle(section, t)}>
      <div className="flex-office-template-grid" style={{ ...flexContainerStyle(section, t), display: 'grid', gridTemplateColumns: '.9fr 1.1fr', gap: 58, alignItems: 'start' }}>
        <div>
          {c.eyebrow && <span style={{ color: t.primary, fontWeight: 800 }}>{c.eyebrow}</span>}
          <h1 style={{ color: t.secondary, fontSize: 'clamp(40px, 5vw, 62px)', lineHeight: 1.08, margin: '12px 0 18px' }}>{c.title}</h1>
          <p style={{ color: t.mutedColor, fontSize: 18, lineHeight: 1.7 }}>{c.subtitle}</p>
          {[{ icon: 'Phone', title: c.phoneTitle, value: c.phoneValue }, { icon: 'Mail', title: c.emailTitle, value: c.emailValue }, { icon: 'MapPin', title: c.addressTitle, value: c.addressValue }].map((item) => <div key={item.title} style={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 14, marginTop: 24 }}><span style={{ color: t.primary }}><FlexOfficeIcon name={item.icon} /></span><div><strong style={{ color: t.secondary }}>{item.title}</strong><p style={{ whiteSpace: 'pre-line', color: t.mutedColor, margin: '4px 0 0' }}>{item.value}</p></div></div>)}
        </div>
        <form onSubmit={inquiryForm.submitInquiry} style={{ background: '#fff', border: `1px solid ${t.borderColor}`, borderRadius: 24, padding: 28, boxShadow: t.cardShadow }}>
          <h2 style={{ color: t.secondary, marginTop: 0 }}>{c.formTitle}</h2>
          <div className="flex-office-template-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {fields.map((field, index) => {
              const Tag = field.type === 'textarea' ? 'textarea' : 'input';
              const fieldName = inquiryForm.fieldNameFromLabel(field.label, field.type);
              return <label key={`${field.label}-${index}`} style={{ display: 'grid', gap: 8, color: t.secondary, fontWeight: 800, gridColumn: field.type === 'textarea' || field.type === 'email' ? '1 / -1' : undefined }}>{field.label}<Tag rows={field.type === 'textarea' ? 5 : undefined} value={inquiryForm.values[fieldName] || ''} onChange={(event) => inquiryForm.setFieldValue(fieldName, event.target.value)} style={{ border: `1px solid ${inquiryForm.errors[fieldName] ? '#dc2626' : t.borderColor}`, borderRadius: 12, padding: 12, minHeight: field.type === 'textarea' ? 120 : 44 }} />{inquiryForm.errors[fieldName] && <span style={{ color: '#dc2626', fontSize: 12 }}>{inquiryForm.errors[fieldName]}</span>}</label>;
            })}
          </div>
          {inquiryForm.status && <p style={{ margin: '14px 0 0', color: inquiryForm.status.type === 'success' ? '#166534' : '#b91c1c', fontWeight: 700 }}>{inquiryForm.status.message}</p>}
          <button type="submit" disabled={inquiryForm.submitting} style={{ marginTop: 18, width: '100%', border: 0, minHeight: 48, borderRadius: t.buttonRadius, background: `linear-gradient(135deg, ${t.primary}, ${t.accent})`, color: '#fff', fontWeight: 800, opacity: inquiryForm.submitting ? .72 : 1 }}>{inquiryForm.submitting ? 'Sending...' : c.submitLabel}</button>
        </form>
      </div>
    </section>
  );
};

const flexOfficeResponsiveCss = `
  [data-template-id] .flex-office-template-grid,
  [data-template-id] .flex-office-template-card-grid,
  [data-template-id] .flex-office-template-filter-row,
  [data-template-id] .flex-office-template-form-grid {
    min-width: 0;
  }
  @media (max-width: 900px) {
    [data-template-id] .flex-office-template-grid,
    [data-template-id] .flex-office-template-card-grid,
    [data-template-id] .flex-office-template-filter-row {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
  @media (max-width: 560px) {
    [data-template-id] .flex-office-template-days-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    }
    [data-template-id] .flex-office-template-form-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }
    [data-template-id] section {
      padding-left: 18px !important;
      padding-right: 18px !important;
    }
  }
`;

export {
  FlexOfficeAboutSection,
  FlexOfficeBookingSearchSection,
  FlexOfficeBookingsSection,
  FlexOfficeContactSection,
  FlexOfficeCtaSection,
  FlexOfficeFeaturesSection,
  FlexOfficeHeroSection,
  FlexOfficeInvoiceSection,
  FlexOfficeServiceDetailSection,
  FlexOfficeServicesPageSection,
  FlexOfficeTimelineSection,
  FlexOfficeWorkingHoursSection,
  flexOfficeResponsiveCss,
};
